import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiCalendarAlt } from 'react-icons/bi';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import inputStyles from '@/components/Inputs/inputs.module.scss';
import { TimeSelect } from '@/components/TimeSelect';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSheetDragToClose } from '@/hooks/useSheetDragToClose';

import { DateRangeField, type DateRangeFieldProps } from './DateRangeField';
import styles from './DateTimePicker.module.scss';
import { buildDayCells, isSameDay, startOfDay, WEEKDAYS } from './days';

type SingleProps = {
  label?: string;
  placeholder: string;
  value?: null | string;
  onChange(value: string): void;
  onRangeChange?: never;
  minDate?: Date;
  maxDate?: Date;
  mandatory?: boolean;
  showTimeInput?: boolean;
  monthPicker?: boolean;
  yearPicker?: boolean;
  error?: string;
  /** Shown under the field when there is no error — a date that is valid but
      worth a second look, such as one in the past. */
  warning?: string;
  /** How a day reads on the trigger: `short` is `Feb 14, 2026`, `slash` is `02/14/2026`. */
  dateFormat?: 'short' | 'slash';
  className?: string;
};

/** Pass `onRangeChange` (with `endValue`) instead of `onChange` and the picker
    takes a start and an end from one calendar, in place. See DateRangeField. */
type DateTimePickerProps = SingleProps | (DateRangeFieldProps & { onChange?: never });

type PanelMode = 'days' | 'months' | 'years';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const YEARS_PER_PAGE = 12;
const POPOVER_WIDTH = 380;
const POPOVER_GAP = 8;
const VIEWPORT_PADDING = 16;
/* The `.sheet` transform transition. */
const SHEET_CLOSE_MS = 220;

const toMeridiem = (date: Date) => (date.getHours() >= 12 ? 'PM' : 'AM');

const toTimeText = (date: Date) => {
  const hours = date.getHours() % 12 || 12;
  return `${hours}:${date.getMinutes().toString().padStart(2, '0')}`;
};

const formatTrigger = (
  date: Date,
  showTimeInput: boolean,
  monthPicker: boolean,
  yearPicker: boolean,
  dateFormat: 'short' | 'slash',
) => {
  if (yearPicker) return String(date.getFullYear());
  if (monthPicker) return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const dayText = date.toLocaleDateString(
    'en-US',
    dateFormat === 'slash'
      ? { month: '2-digit', day: '2-digit', year: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
  );

  return showTimeInput ? `${dayText}, ${toTimeText(date)} ${toMeridiem(date)}` : dayText;
};

const DatePickerPanel = ({
  initialDate,
  minDate,
  maxDate,
  showTimeInput,
  monthPicker,
  yearPicker,
  onConfirm,
}: {
  initialDate: Date;
  minDate?: Date;
  maxDate?: Date;
  showTimeInput: boolean;
  monthPicker: boolean;
  yearPicker: boolean;
  onConfirm: (date: Date) => void;
}) => {
  const [draft, setDraft] = useState(initialDate);
  const [view, setView] = useState(
    () => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1),
  );
  const [mode, setMode] = useState<PanelMode>(
    yearPicker ? 'years' : monthPicker ? 'months' : 'days',
  );

  const today = new Date();

  const isDayDisabled = (date: Date) => {
    if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
    if (maxDate && startOfDay(date) > startOfDay(maxDate)) return true;
    return false;
  };

  const isMonthDisabled = (year: number, month: number) => {
    if (minDate && new Date(year, month + 1, 0) < startOfDay(minDate)) return true;
    if (maxDate && new Date(year, month, 1) > startOfDay(maxDate)) return true;
    return false;
  };

  const isYearDisabled = (year: number) => {
    if (minDate && new Date(year, 11, 31) < startOfDay(minDate)) return true;
    if (maxDate && new Date(year, 0, 1) > startOfDay(maxDate)) return true;
    return false;
  };

  const shiftView = (step: number) => {
    setView((current) => {
      if (mode === 'days') return new Date(current.getFullYear(), current.getMonth() + step, 1);
      if (mode === 'months') return new Date(current.getFullYear() + step, current.getMonth(), 1);
      return new Date(current.getFullYear() + step * YEARS_PER_PAGE, current.getMonth(), 1);
    });
  };

  const selectDay = (date: Date) => {
    const next = new Date(date);
    if (showTimeInput) next.setHours(draft.getHours(), draft.getMinutes(), 0, 0);
    else next.setHours(0, 0, 0, 0);
    setDraft(next);
  };

  const headerLabel = () => {
    if (mode === 'days')
      return view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (mode === 'months') return String(view.getFullYear());
    const start = Math.floor(view.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE;
    return `${start} – ${start + YEARS_PER_PAGE - 1}`;
  };

  const handleHeaderClick = () => {
    if (mode === 'days') setMode('months');
    else if (mode === 'months') setMode('years');
  };

  const selectMonth = (month: number) => {
    const year = view.getFullYear();
    setView(new Date(year, month, 1));

    if (monthPicker) setDraft(new Date(year, month, 1));
    else setMode('days');
  };

  const yearsPage = () => {
    const start = Math.floor(view.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE;
    return Array.from({ length: YEARS_PER_PAGE }, (_, index) => start + index);
  };

  return (
    <>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.headerLabel}
          onClick={handleHeaderClick}
          disabled={mode === 'years'}
        >
          {headerLabel()}
          {mode !== 'years' && <IoChevronForward />}
        </button>
        <div className={styles.nav}>
          <button type="button" onClick={() => shiftView(-1)} aria-label="Previous">
            <IoChevronBack />
          </button>
          <button type="button" onClick={() => shiftView(1)} aria-label="Next">
            <IoChevronForward />
          </button>
        </div>
      </div>

      {mode === 'days' && (
        <>
          <div className={styles.weekdays}>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className={styles.days}>
            {buildDayCells(view).map((date, index) =>
              date ? (
                <button
                  key={date.toISOString()}
                  type="button"
                  className={clsx(
                    styles.day,
                    startOfDay(date).getTime() === startOfDay(draft).getTime() &&
                      styles.daySelected,
                    isSameDay(date, today) && styles.dayToday,
                  )}
                  disabled={isDayDisabled(date)}
                  onClick={() => selectDay(date)}
                >
                  {date.getDate()}
                </button>
              ) : (
                <span key={`empty-${index}`} />
              ),
            )}
          </div>
        </>
      )}

      {mode === 'months' && (
        <div className={styles.grid}>
          {MONTHS_SHORT.map((month, index) => (
            <button
              key={month}
              type="button"
              className={clsx(
                styles.gridItem,
                index === draft.getMonth() &&
                  view.getFullYear() === draft.getFullYear() &&
                  styles.gridItemSelected,
              )}
              disabled={isMonthDisabled(view.getFullYear(), index)}
              onClick={() => selectMonth(index)}
            >
              {month}
            </button>
          ))}
        </div>
      )}

      {mode === 'years' && (
        <div className={styles.grid}>
          {yearsPage().map((year) => (
            <button
              key={year}
              type="button"
              className={clsx(
                styles.gridItem,
                year === draft.getFullYear() && styles.gridItemSelected,
              )}
              disabled={isYearDisabled(year)}
              onClick={() => {
                setView(new Date(year, view.getMonth(), 1));
                if (yearPicker) setDraft(new Date(year, 0, 1));
                else setMode('months');
              }}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {showTimeInput && (
        <TimeSelect
          className={styles.timeRow}
          label="Time"
          id="datetimepicker-time"
          value={draft}
          onChange={setDraft}
        />
      )}

      <button type="button" className={styles.confirm} onClick={() => onConfirm(draft)}>
        Confirm
      </button>
    </>
  );
};

const MobileSheet = ({
  onCancel,
  children,
}: {
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // A swipe closes like ✕: the draft is dropped. It only waits for the sheet to slide out.
  const { dragY, isDragging, onDragStart } = useSheetDragToClose({
    onClose: () => {
      setIsOpen(false);
      setTimeout(onCancel, SHEET_CLOSE_MS);
    },
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return createPortal(
    <div className={clsx(styles.sheetRoot, isOpen && styles.sheetOpen)}>
      <div className={styles.backdrop} onClick={onCancel} />
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label="Select date"
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : undefined,
        }}
      >
        <div className={styles.dragArea} onPointerDown={onDragStart}>
          <div className={styles.handle} />
          <button type="button" className={styles.close} onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

const DesktopPopover = ({
  anchor,
  onCancel,
  children,
}: {
  anchor: HTMLElement | null;
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchor?.contains(target)) return;
      /* A control inside the panel may portal its own layer to <body> — the time
         list does. Without this, choosing a time read as a click outside and shut
         the picker before the choice was applied. */
      if (target instanceof Element && target.closest('[data-floating-child]')) return;
      onCancel();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [anchor, onCancel]);

  const setPanelNode = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (!node || !anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const panelHeight = node.offsetHeight;
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const top =
        spaceBelow < panelHeight + POPOVER_GAP + VIEWPORT_PADDING
          ? Math.max(VIEWPORT_PADDING, anchorRect.top - panelHeight - POPOVER_GAP)
          : anchorRect.bottom + POPOVER_GAP;
      const left = Math.max(
        VIEWPORT_PADDING,
        Math.min(anchorRect.left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING),
      );

      setPosition({ left, top });
    },
    [anchor],
  );

  return createPortal(
    <div
      ref={setPanelNode}
      className={styles.popover}
      role="dialog"
      aria-label="Select date"
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const SingleField = ({
  label,
  placeholder,
  value,
  onChange,
  minDate,
  maxDate,
  mandatory,
  showTimeInput = true,
  monthPicker = false,
  yearPicker = false,
  error,
  warning,
  dateFormat = 'short',
  className,
}: SingleProps) => {
  const isMobile = useIsMobile();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const isPickerOpen = Boolean(anchor);

  const selected = value ? new Date(value) : null;

  const initialDate = () => {
    if (selected) return selected;
    const base = minDate && minDate > new Date() ? new Date(minDate) : new Date();
    if (showTimeInput) base.setSeconds(0, 0);
    else base.setHours(0, 0, 0, 0);
    if (monthPicker) base.setDate(1);
    if (yearPicker) base.setMonth(0, 1);
    return base;
  };

  const panel = (
    <DatePickerPanel
      initialDate={initialDate()}
      minDate={minDate}
      maxDate={maxDate}
      showTimeInput={showTimeInput && !monthPicker && !yearPicker}
      monthPicker={monthPicker}
      yearPicker={yearPicker}
      onConfirm={(date) => {
        onChange(date.toISOString());
        setAnchor(null);
      }}
    />
  );

  return (
    <div className={clsx(styles.field, className)}>
      {label && (
        <span className={styles.fieldLabel}>
          {label}
          {mandatory && <span>*</span>}
        </span>
      )}
      <button
        type="button"
        className={clsx(styles.trigger, error && styles.triggerError)}
        onClick={(event) => {
          const trigger = event.currentTarget;
          setAnchor((current) => (current ? null : trigger));
        }}
      >
        <span className={clsx(!selected && styles.triggerPlaceholder)}>
          {selected
            ? formatTrigger(
                selected,
                showTimeInput && !monthPicker && !yearPicker,
                monthPicker,
                yearPicker,
                dateFormat,
              )
            : placeholder}
        </span>
        <BiCalendarAlt size={20} />
      </button>
      {error && <p className={inputStyles.Error}>{error}</p>}
      {!error && warning && (
        <p className={inputStyles.Warning}>
          <AiOutlineWarning size={14} />
          {warning}
        </p>
      )}
      {isPickerOpen &&
        (isMobile ? (
          <MobileSheet onCancel={() => setAnchor(null)}>{panel}</MobileSheet>
        ) : (
          <DesktopPopover anchor={anchor} onCancel={() => setAnchor(null)}>
            {panel}
          </DesktopPopover>
        ))}
    </div>
  );
};

/* Switched before any hook, so each mode keeps its own hook order. */
const DateTimePicker = (props: DateTimePickerProps) =>
  props.onRangeChange ? <DateRangeField {...props} /> : <SingleField {...props} />;

export default DateTimePicker;
